import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { CqmFilterClient, CqmFilteredExportRequest } from '@medone/medonehp-api-client';

import { Axios } from '../../../shared/common/http';
import { RootState } from '../../../shared/store';
import { CqmFilterCriteria, CqmFilterState, initialCqmFilterState, QrdaReportType } from './filter-models';
import { buildPeriod } from './lib/periods';

const buildClient = () => new CqmFilterClient(null as any, Axios);

// rejectWithValue (not the default serialized action.error) so a 400 (AgeMin>AgeMax) / 404
// (unknown measure) status survives to the component — the generated client throws ApiException
// carrying status + parsed ProblemDetails.
export const fetchCqmFilter = createAsyncThunk('cqmFilter/fetch', async (criteria: CqmFilterCriteria, { rejectWithValue }) => {
    try {
        const response = await buildClient().getFiltered(
            criteria.measure,
            criteria.year,
            criteria.providerNpis,
            criteria.providerTins,
            criteria.providerTaxonomyCodes,
            criteria.facilityIds,
            criteria.payerCodes,
            criteria.coverageTypes,
            criteria.ageMin,
            criteria.ageMax,
            criteria.sex,
            // sexSnomedCode — the panel captures sex via the Genders enum, not the SNOMED wire form.
            undefined,
            criteria.raceCodes,
            criteria.ethnicityCodes,
            criteria.problemSnomedCodes,
            criteria.problemIcd10Codes,
            criteria.isFromImport
        );
        if (response.result.succeeded && response.result.entity) {
            return response.result.entity;
        }
        return rejectWithValue({ status: null, message: response.result.message || 'Failed to run the filter.' });
    } catch (err: any) {
        return rejectWithValue({ status: err?.status ?? null, message: err?.result?.detail ?? err?.message ?? 'Failed to run the filter.' });
    }
});

// Export the (c)(4) filtered cohort as QRDA. The backend resolves the cohort + cap + NoMatch, so this calls
// exportFiltered (NOT createReport). CohortTooLarge comes back as a 400 (ApiException) — rejectWithValue
// preserves its ProblemDetails detail; Created/NoMatch resolve as a normal 200 (branch on entity.outcome).
export const exportFilteredCohort = createAsyncThunk(
    'cqmFilter/export',
    async ({ criteria, reportType }: { criteria: CqmFilterCriteria; reportType: QrdaReportType }, { rejectWithValue }) => {
        try {
            const period = buildPeriod(criteria.year ?? new Date().getFullYear());
            const req = CqmFilteredExportRequest.fromJS({
                // Filter fields only — measure/year map to measureCmsIds/period below, and the backend
                // ignores isFromImport for exports (it scopes result rows, not the cohort).
                criteria: {
                    providerNpis: criteria.providerNpis,
                    providerTins: criteria.providerTins,
                    providerTaxonomyCodes: criteria.providerTaxonomyCodes,
                    facilityIds: criteria.facilityIds,
                    payerCodes: criteria.payerCodes,
                    coverageTypes: criteria.coverageTypes,
                    ageMin: criteria.ageMin,
                    ageMax: criteria.ageMax,
                    sex: criteria.sex,
                    raceCodes: criteria.raceCodes,
                    ethnicityCodes: criteria.ethnicityCodes,
                    problemSnomedCodes: criteria.problemSnomedCodes,
                    problemIcd10Codes: criteria.problemIcd10Codes,
                },
                startDate: period.startDate,
                endDate: period.endDate,
                measureCmsIds: [criteria.measure],
                reportType,
                skipCypress: false,
            });
            const response = await buildClient().exportFiltered(req);
            if (response.result.succeeded && response.result.entity) {
                return response.result.entity;
            }
            return rejectWithValue({ status: null, message: response.result.message || 'Failed to export the cohort.' });
        } catch (err: any) {
            return rejectWithValue({ status: err?.status ?? null, message: err?.result?.detail ?? err?.result?.title ?? err?.message ?? 'Failed to export the cohort.' });
        }
    }
);

const slice = createSlice({
    name: 'cqmFilter',
    initialState: initialCqmFilterState,
    reducers: {
        resetCqmFilter: () => initialCqmFilterState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCqmFilter.pending, (state: CqmFilterState, action) => {
                state.loading = true;
                state.errorMessage = null;
                state.applied = action.meta.arg;
            })
            .addCase(fetchCqmFilter.fulfilled, (state: CqmFilterState, action) => {
                state.loading = false;
                state.result = action.payload;
            })
            .addCase(fetchCqmFilter.rejected, (state: CqmFilterState, action) => {
                state.loading = false;
                state.errorMessage = (action.payload as { message?: string } | undefined)?.message ?? action.error.message ?? 'Failed to run the filter.';
            })
            .addCase(exportFilteredCohort.pending, (state: CqmFilterState) => {
                state.exporting = true;
                state.exportError = null;
            })
            .addCase(exportFilteredCohort.fulfilled, (state: CqmFilterState) => {
                state.exporting = false;
            })
            .addCase(exportFilteredCohort.rejected, (state: CqmFilterState, action) => {
                state.exporting = false;
                state.exportError = (action.payload as { message?: string } | undefined)?.message ?? action.error.message ?? 'Failed to export the cohort.';
            });
    },
});

export const { resetCqmFilter } = slice.actions;

export const selectCqmFilterResult = (state: RootState) => (state as any).cqmFilter?.result ?? null;
export const selectCqmFilterLoading = (state: RootState) => (state as any).cqmFilter?.loading ?? false;
export const selectCqmFilterError = (state: RootState) => (state as any).cqmFilter?.errorMessage ?? null;
export const selectCqmExporting = (state: RootState) => (state as any).cqmFilter?.exporting ?? false;
export const selectCqmExportError = (state: RootState) => (state as any).cqmFilter?.exportError ?? null;
export const selectCqmApplied = (state: RootState) => (state as any).cqmFilter?.applied ?? null;

export default slice.reducer;
